import { formatInTimeZone } from 'date-fns-tz';

/**
 * Notes attach to an event occurrence by the Berlin-local day of their
 * occurredAt (see crm-event-query-many-details / event-details-dialog).
 */
export const eventNoteDayTz = 'Europe/Berlin';

export function eventNoteDay(occurredAt: Date): string {
    return formatInTimeZone(occurredAt, eventNoteDayTz, 'yyyy-MM-dd');
}

/** Berlin-local day of the newest note in the list, or undefined when empty. */
export function latestEventNoteDay(notes: { occurredAt: Date }[]): string | undefined {
    return notes
        .map((note) => eventNoteDay(note.occurredAt))
        .toSorted()
        .at(-1);
}

type EventOccurrenceLockInput = {
    isRecurring: boolean;
    /** Berlin-local day ('yyyy-MM-dd') of the newest attached note, undefined when none. */
    latestNoteDate: string | undefined;
    /**
     * The occurrence being edited/moved/deleted ('yyyy-MM-dd'). Omit when the
     * operation affects the whole event/series (full delete, series rewrite).
     */
    occurrenceDate?: string;
};

/**
 * STEER-121: an event↔note link only survives while the occurrence keeps its
 * date, so occurrences with logged notes are locked against editing, moving,
 * and deleting.
 *
 * - Recurring series: the occurrence on date D is locked when a note is
 *   attached on D or any later day (changing D would re-shape the series the
 *   notes hang on).
 * - Single events and whole-event/series operations: any attached note locks.
 *
 * The API enforces the same rule (crm-event-assert-not-locked); the UI uses
 * this to hide/disable the controls up front.
 */
export function isEventOccurrenceLocked({
    isRecurring,
    latestNoteDate,
    occurrenceDate,
}: EventOccurrenceLockInput): boolean {
    if (!latestNoteDate) {
        return false;
    }

    if (!isRecurring || occurrenceDate === undefined) {
        return true;
    }

    return occurrenceDate <= latestNoteDate;
}
